import type { CSSProperties } from 'react';
import type { PlantafelDayHours, PlantafelVisibleWeekdays } from '../../utils/plantafelSettings';
import { DEFAULT_PLANTAFEL_DISPLAY_SETTINGS } from '../../utils/plantafelSettings';
import { getViewSpan } from './mapActivityToBlock';
import type { GridBlock, PlantafelViewMode } from './plantafelTypes';
import { PLANTAFEL_ROW_BASE_H, PLANTAFEL_ROW_LANE_EXTRA_H } from './plantafelTypes';

export type BlockStackLayout = {
  laneIndex: number;
  laneCount: number;
  rowHeight: number;
};

type PctSpan = { id: string; leftPct: number; widthPct: number };

function spansOverlap(a: PctSpan, b: PctSpan): boolean {
  return a.leftPct < b.leftPct + b.widthPct && b.leftPct < a.leftPct + a.widthPct;
}

/** Überlappende Blöcke in vertikale Lanes aufteilen und Zeilenhöhe berechnen. */
export function computeRowStackLayout(
  blocks: GridBlock[],
  viewMode: PlantafelViewMode,
  monthDayCount?: number,
  visibleWeekdays?: PlantafelVisibleWeekdays,
  dayHours: PlantafelDayHours = DEFAULT_PLANTAFEL_DISPLAY_SETTINGS.dayHours,
): { rowHeight: number; lanesByBlockId: Map<string, BlockStackLayout> } {
  const spans: PctSpan[] = [];
  for (const b of blocks) {
    const span = getViewSpan(b, viewMode, monthDayCount, visibleWeekdays, dayHours);
    if (!span) continue;
    spans.push({ id: b.id, ...span });
  }

  const lanesByBlockId = new Map<string, BlockStackLayout>();
  if (spans.length === 0) {
    return { rowHeight: PLANTAFEL_ROW_BASE_H, lanesByBlockId };
  }

  spans.sort((a, b) => a.leftPct - b.leftPct || b.widthPct - a.widthPct);

  const lanes: PctSpan[][] = [];
  for (const span of spans) {
    let placed = false;
    for (let i = 0; i < lanes.length; i++) {
      if (!lanes[i].some((other) => spansOverlap(span, other))) {
        lanes[i].push(span);
        placed = true;
        break;
      }
    }
    if (!placed) lanes.push([span]);
  }

  const laneCount = lanes.length;
  const rowHeight = PLANTAFEL_ROW_BASE_H + (laneCount - 1) * PLANTAFEL_ROW_LANE_EXTRA_H;

  for (let i = 0; i < lanes.length; i++) {
    for (const span of lanes[i]) {
      lanesByBlockId.set(span.id, { laneIndex: i, laneCount, rowHeight });
    }
  }

  return { rowHeight, lanesByBlockId };
}

export const ROW_STACK_GUTTER_PX = 3;
export const ROW_STACK_LANE_GAP_PX = 2;

export function blockStackPositionStyle(
  stack: BlockStackLayout | undefined,
): Pick<CSSProperties, 'top' | 'height' | 'bottom'> {
  if (!stack) {
    return { top: ROW_STACK_GUTTER_PX, bottom: ROW_STACK_GUTTER_PX };
  }
  const { laneIndex, laneCount, rowHeight } = stack;
  const inner = rowHeight - ROW_STACK_GUTTER_PX * 2 - ROW_STACK_LANE_GAP_PX * (laneCount - 1);
  const laneH = inner / laneCount;
  return {
    top: ROW_STACK_GUTTER_PX + laneIndex * (laneH + ROW_STACK_LANE_GAP_PX),
    height: laneH,
  };
}
